// The pure model behind S3's import tab and S4's import detail (Module 02 §3.3,
// §2.6/§2.7, §7). No React, no network — so the state machine, the mapping
// rules, the redaction rule and the problem-code translation are unit-tested
// without a DOM.
//
// Everything here MIRRORS server truth; none of it decides anything:
//  * the status predicates mirror the exact guards in
//    apps/api/.../import.service.ts (parse: uploaded; mapping patch:
//    mapping|validated; validate: mapping; commit: validated; discard: anything
//    but committed/committing/discarded). The server 409s IMPORT_STATE_INVALID
//    regardless — these only stop the UI offering a button that cannot work.
//  * `TARGET_FIELDS` / `REQUIRED_TARGET_FIELDS` mirror `KNOWN_FIELDS` /
//    `REQUIRED_FIELDS` in apps/api/.../import/import.fields.ts. Only the lanes
//    whose owning module is WIRED have a catalog here (the catalogue ones,
//    `customers` and `open_sales_orders`); the rest map free-text, so no
//    field name is invented for a module that has not shipped. The mirror is
//    held to the server's file by `__tests__/import-model.test.ts`, which
//    reads both of its lists and names the one server lane this file does not
//    mirror yet (`opening_stock`) with its reason.
//  * redaction is applied SERVER-side (§7): money columns arrive `null` with
//    their path named in `redacted_fields[]`. `isRedactedCell` reads that
//    envelope — the client never decides what to hide, only how to show a lock.

import type { components } from "@assessly/api-client";
import { RequestError } from "../../../../lib/request";

type S = components["schemas"];

export type ImportJob = S["ImportJob"];
export type ImportKind = ImportJob["kind"];
export type ImportJobStatus = ImportJob["status"];
export type DuplicatePolicy = ImportJob["duplicate_policy"];
export type ImportRow = S["ImportRow"];
export type ImportRowStatus = ImportRow["status"];
export type ImportRowMessage = ImportRow["messages"][number];

/** D-025 upload caps, enforced authoritatively at parse (§9.15). */
export const MAX_FILE_BYTES = 209_715_200;
export const MAX_FILE_MB = Math.round(MAX_FILE_BYTES / (1024 * 1024));
export const MAX_ROWS = 1_000_000;

/** Extensions the parser accepts (§9.3: .xlsx / .xlsm / UTF-8 or CP1251 CSV). */
export const ACCEPTED_EXTENSIONS = [".xlsx", ".xlsm", ".csv"] as const;

/** The eleven kinds (§2.6), in the order the wizard offers them. */
export const ALL_IMPORT_KINDS: readonly ImportKind[] = [
  "products",
  "materials",
  "boms",
  "routings",
  "customers",
  "suppliers",
  "opening_stock",
  "open_sales_orders",
  "open_purchase_orders",
  "machines",
  "people",
];

/** The kinds step 2 loads (§3.3 "Step 2 — Catalogue entry"). */
export const CATALOGUE_IMPORT_KINDS: readonly ImportKind[] = [
  "products",
  "materials",
  "boms",
  "routings",
];

/** The kinds step 4 loads (§3.5 "Opening state"). */
export const OPENING_IMPORT_KINDS: readonly ImportKind[] = [
  "opening_stock",
  "customers",
  "suppliers",
  "open_sales_orders",
  "open_purchase_orders",
];

export const DUPLICATE_POLICIES: readonly DuplicatePolicy[] = ["reject", "update", "skip"];

/**
 * Which STEP a job belongs to, so "back" and the post-commit CTA return the user
 * where they started. A `customers` job reached from step 4 must not send them
 * to step 2 (§4 S3/S7 both open the same import screens).
 */
export function stepRouteForKind(
  kind: ImportKind,
): "/onboarding/catalogue" | "/onboarding/opening" {
  return OPENING_IMPORT_KINDS.includes(kind) ? "/onboarding/opening" : "/onboarding/catalogue";
}

/** The money paths §7 redacts. Kept for the "which column is a money column"
 * question only — whether a given response redacted them is answered by
 * `redacted_fields[]`, never by this list. */
export const MONEY_FIELDS = ["value_minor", "unit_cost_minor", "price_minor"] as const;

// ---------------------------------------------------------------------------
// Field catalog (mirror of the service's KNOWN_FIELDS / REQUIRED_FIELDS)
// ---------------------------------------------------------------------------

const PRODUCT_FIELDS = [
  "sku",
  "name",
  "kind",
  "base_uom",
  "attribute_schema",
  "tracking",
  "make_or_buy",
  "price_unit_qty",
] as const;

/**
 * The `customers` lane (module 09's `CustomersImportHandler`, spec 09 §3.15's
 * Columns paragraph, spec 02 §3.3's customers row, D-325 rulings 3 and 8), in
 * the server's order. `customer_no` is the key and every other column a field
 * of the create or update the row sends. `credit_limit` is a decimal in the
 * row's currency, so the book's export column `credit_limit_minor` is
 * deliberately not a field. `tags` holds active tag names split on `;`,
 * `notes` becomes the customer's pinned note, and the six `billing_*` cells
 * are parts of one billing address. The parse auto-maps a Russian workbook
 * through the server's synonym tables, so the editor mostly confirms.
 */
const CUSTOMER_FIELDS = [
  "customer_no",
  "legal_name",
  "tier",
  "display_name",
  "tax_id",
  "country",
  "default_currency",
  "credit_limit",
  "credit_terms_days",
  "preferred_channel",
  "preferred_locale",
  "owner_email",
  "contact_name",
  "contact_position",
  "phone",
  "email",
  "tags",
  "notes",
  "billing_line1",
  "billing_line2",
  "billing_city",
  "billing_region",
  "billing_postal",
  "billing_country",
] as const;

/** The `open_sales_orders` lane (spec 09 §3.15b, D-353 ruling 2), in the
 * server's order. Rows group by `order_ref`; one of `customer_no` and `tax_id`
 * names the customer, which the handler judges per row. */
const OPEN_SALES_ORDER_FIELDS = [
  "order_ref",
  "customer_no",
  "tax_id",
  "product_sku",
  "qty",
  "uom",
  "unit_price",
  "currency",
  "promise_date",
  "facility_code",
  "customer_po_ref",
  "ordered_on",
] as const;

export const TARGET_FIELDS: Readonly<Partial<Record<ImportKind, readonly string[]>>> = {
  customers: CUSTOMER_FIELDS,
  open_sales_orders: OPEN_SALES_ORDER_FIELDS,
  products: PRODUCT_FIELDS,
  // Materials are products with a material `kind` in Module 01 (D-036), so the
  // lane shares the product field catalog.
  materials: PRODUCT_FIELDS,
  boms: [
    "product_sku",
    "version",
    "component_sku",
    "qty",
    "uom",
    "line_no",
    "scrap_pct",
    "consume_at_step_no",
    "is_optional",
    "facility_id",
    "output_qty",
    "output_uom",
    "is_phantom",
  ],
  routings: [
    "product_sku",
    "version",
    "name",
    "step_no",
    "work_center",
    "run_minutes",
    "setup_minutes",
    "run_basis",
    "batch_qty",
    "setup_key",
    "operation_scrap_pct",
    "facility_id",
  ],
};

export const REQUIRED_TARGET_FIELDS: Readonly<Partial<Record<ImportKind, readonly string[]>>> = {
  products: ["sku", "name", "kind", "base_uom"],
  materials: ["sku", "name", "kind", "base_uom"],
  boms: ["product_sku", "component_sku", "qty", "uom", "line_no"],
  routings: ["product_sku", "name", "step_no", "work_center", "run_minutes"],
  // spec 09 §3.15 / D-325 ruling 8: a job with either unmapped answers 422
  // MAPPING_INCOMPLETE at validate. `customer_no` is optional (a row with no
  // key creates), and an empty `display_name` takes the trimmed legal name.
  customers: ["legal_name", "tier"],
  open_sales_orders: ["order_ref", "product_sku", "qty", "unit_price"],
};

/**
 * The field that groups a grouped lane's rows into one commit unit (spec 02
 * §3.3 step 5). The row grid leads with its column so each row's group shows.
 */
export const GROUP_KEY_FIELDS: Readonly<Partial<Record<ImportKind, string>>> = {
  open_sales_orders: "order_ref",
};

/** The sheet's columns with the group key's column first, when the lane has
 * one and the mapping names it. */
export function groupFirstHeaders(
  kind: ImportKind,
  mapping: Readonly<Record<string, string>>,
  headers: readonly string[],
): string[] {
  const key = GROUP_KEY_FIELDS[kind];
  const source = Object.entries(mapping).find(([, target]) => target === key)?.[0];
  if (key === undefined || source === undefined || !headers.includes(source)) return [...headers];
  return [source, ...headers.filter((header) => header !== source)];
}

/** True when this lane has a published field catalog. A lane without one maps
 * free-text until its owning module ships (§3.3 "commits via" column). */
export function hasFieldCatalog(kind: ImportKind): boolean {
  return (TARGET_FIELDS[kind]?.length ?? 0) > 0;
}

/** The lanes whose records carry the pack's attribute set (products and the
 * materials that share their catalog, D-036), and so the only templates that
 * gain its `attr.*` columns. */
export const ATTRIBUTE_TEMPLATE_KINDS: readonly ImportKind[] = ["products", "materials"];

/** Attribute columns pass straight through (`attr.<key>` maps to itself). */
export function isAttributeColumn(header: string): boolean {
  return header.startsWith("attr.");
}

/** The target options offered for one source header (catalog + itself when it
 * is an `attr.` column, which the server auto-maps to itself). */
export function targetOptionsFor(kind: ImportKind, header: string): readonly string[] {
  const catalog = TARGET_FIELDS[kind] ?? [];
  return isAttributeColumn(header) && !catalog.includes(header) ? [header, ...catalog] : catalog;
}

// ---------------------------------------------------------------------------
// Column mapping
// ---------------------------------------------------------------------------

export interface MappingIssues {
  /** Required targets no column maps to — mirrors 422 MAPPING_INCOMPLETE. */
  readonly missingRequired: readonly string[];
  /** Targets two or more columns both claim (last-wins server-side: refuse it). */
  readonly duplicateTargets: readonly string[];
  readonly ok: boolean;
}

/** What is wrong with a proposed mapping, before it is sent. */
export function mappingIssues(kind: ImportKind, mapping: Record<string, string>): MappingIssues {
  const counts = new Map<string, number>();
  for (const target of Object.values(mapping)) {
    if (target.length === 0) continue;
    counts.set(target, (counts.get(target) ?? 0) + 1);
  }
  const missingRequired = (REQUIRED_TARGET_FIELDS[kind] ?? []).filter((f) => !counts.has(f));
  const duplicateTargets = [...counts.entries()].filter(([, n]) => n > 1).map(([t]) => t);
  return {
    missingRequired,
    duplicateTargets,
    ok: missingRequired.length === 0 && duplicateTargets.length === 0,
  };
}

/** Drop the empty ("not imported") entries — the wire format carries only real
 * `source_header → target_field` pairs (§2.6 column_mapping). */
export function cleanMapping(mapping: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [source, target] of Object.entries(mapping)) {
    if (target.length > 0) out[source] = target;
  }
  return out;
}

/**
 * Header order as the sheet had it: the parsed rows preserve it (raw is keyed by
 * the original header), and any mapped header not in the sample is appended so a
 * column can never disappear from the editor.
 */
export function sourceHeaders(
  mapping: Record<string, string>,
  rows: readonly ImportRow[],
): string[] {
  const seen = new Set<string>();
  const headers: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row.raw)) {
      if (!seen.has(key)) {
        seen.add(key);
        headers.push(key);
      }
    }
  }
  for (const key of Object.keys(mapping)) {
    if (!seen.has(key)) {
      seen.add(key);
      headers.push(key);
    }
  }
  return headers;
}

/** First non-empty sample value for a column, for the mapping editor. */
export function sampleValue(header: string, rows: readonly ImportRow[]): string {
  for (const row of rows) {
    const value = row.raw[header];
    if (value === null || value === undefined) continue;
    const text = cellText(value);
    if (text.length > 0) return text;
  }
  return "";
}

/** Render one raw cell without ever printing `[object Object]`. */
export function cellText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  return JSON.stringify(value);
}

// ---------------------------------------------------------------------------
// Status machine (mirror of the service guards)
// ---------------------------------------------------------------------------

export function canParse(status: ImportJobStatus): boolean {
  return status === "uploaded";
}
export function canEditMapping(status: ImportJobStatus): boolean {
  return status === "mapping" || status === "validated";
}
export function canValidate(status: ImportJobStatus): boolean {
  return status === "mapping";
}
export function canCommit(status: ImportJobStatus): boolean {
  return status === "validated";
}
export function canDiscard(status: ImportJobStatus): boolean {
  return status !== "committed" && status !== "committing" && status !== "discarded";
}
/** An async worker stage — the detail screen polls while this is true (§3.3). */
export function isWorking(status: ImportJobStatus): boolean {
  return status === "parsing" || status === "committing";
}
/** Terminal: nothing more will happen to this job on its own. */
export function isTerminal(status: ImportJobStatus): boolean {
  return status === "committed" || status === "failed" || status === "discarded";
}

/** Rows only exist once the file has been read. */
export function hasRows(status: ImportJobStatus): boolean {
  return status !== "uploaded" && status !== "parsing";
}

export interface CommitOutcome {
  readonly committed: number;
  readonly skipped: number;
  readonly errors: number;
  readonly total: number;
}

/**
 * True once the commit worker has actually run, so `committed_rows` means
 * something. Before that `commitOutcome().skipped` is arithmetic on a zero and
 * must NOT be shown — a freshly validated 120-row file would otherwise read
 * "OK 114 / Skipped 114" at exactly the moment the user decides whether to
 * press Commit.
 */
export function hasCommitOutcome(status: ImportJobStatus): boolean {
  return status === "committed";
}

/**
 * The committed / skipped / failed split S4 reports (§3.3.5). `skipped` is what
 * the file had that neither landed nor errored — derived, never guessed at: the
 * three numbers always add up to the row count the server parsed.
 *
 * Only meaningful AFTER the commit — guard every caller with
 * {@link hasCommitOutcome}.
 */
export function commitOutcome(
  job: Pick<ImportJob, "total_rows" | "committed_rows" | "error_rows">,
): CommitOutcome {
  const committed = Math.max(0, job.committed_rows);
  const errors = Math.max(0, job.error_rows);
  const total = Math.max(0, job.total_rows);
  return { committed, errors, total, skipped: Math.max(0, total - committed - errors) };
}

/** Fraction 0–1 of the commit worker's progress, for the §4 progress bar. */
export function commitProgress(job: Pick<ImportJob, "total_rows" | "committed_rows">): number {
  if (job.total_rows <= 0) return 0;
  return Math.min(1, Math.max(0, job.committed_rows / job.total_rows));
}

// ---------------------------------------------------------------------------
// §7 redaction
// ---------------------------------------------------------------------------

/**
 * True when the API withheld this cell for this caller. The envelope names the
 * path it nulled (`raw.value_minor`), so a genuinely empty cell and a redacted
 * one are never confused — and a redacted cell is NEVER shown as 0 or blank.
 */
export function isRedactedCell(
  redactedFields: readonly string[],
  scope: "raw" | "normalized",
  field: string,
  value: unknown,
): boolean {
  return value === null && redactedFields.includes(`${scope}.${field}`);
}

/** Any redaction at all on this page — drives the one-line explanation. */
export function hasRedaction(redactedFields: readonly string[]): boolean {
  return redactedFields.length > 0;
}

// ---------------------------------------------------------------------------
// Message + problem translation (never render a raw code — D-022)
// ---------------------------------------------------------------------------

/** i18n key for a row message. Codes are compared case-insensitively because
 * the validators emit UPPER_SNAKE (`UNKNOWN_UOM`) while §2.7's catalog is
 * lowercase (`unknown_unit`); both live in the same dictionary block. */
export function rowMessageKey(code: string): string {
  return `onboarding.importShared.messages.${code.toLowerCase()}`;
}

/** The lanes whose row codes read differently from the shared sentence: the
 * same `CUSTOMER_NOT_FOUND` asks a customers row to leave the key empty and an
 * orders row to fix it. */
export const KIND_MESSAGE_KINDS: readonly ImportKind[] = ["open_sales_orders"];

/** The lane's own sentence for a row code, looked up before the shared one. */
export function kindMessageKey(kind: ImportKind, code: string): string | undefined {
  return KIND_MESSAGE_KINDS.includes(kind)
    ? `onboarding.importShared.kindMessages.${kind}.${code.toLowerCase()}`
    : undefined;
}

/** The classified failure S4/S3 render. `kind` selects the copy; `params` feeds
 * its placeholders; `fields` lists the offending targets when there are any. */
export interface ImportProblem {
  readonly kind:
    | "has_errors"
    | "mapping_incomplete"
    | "kind_not_available"
    | "file_too_large"
    | "bad_encoding"
    | "empty_file"
    | "merged_cells"
    | "finance_required"
    | "state_invalid"
    | "not_found"
    | "facility_required"
    | "forbidden"
    | "importer_not_permitted"
    | "generic";
  readonly fields: readonly string[];
  readonly ownerModule: string | undefined;
  readonly requestId: string | undefined;
}

const PROBLEM_KINDS: Readonly<Record<string, ImportProblem["kind"]>> = {
  has_errors: "has_errors",
  mapping_incomplete: "mapping_incomplete",
  import_kind_not_available: "kind_not_available",
  file_too_large: "file_too_large",
  bad_encoding: "bad_encoding",
  empty_file: "empty_file",
  merged_cells: "merged_cells",
  finance_required_for_valued_stock: "finance_required",
  import_state_invalid: "state_invalid",
  import_job_not_found: "not_found",
  facility_required: "facility_required",
  permission_denied: "forbidden",
  // P3-E2 PR E2-2c (spec 09 §3.15 and §4.18, D-325 ruling 2): 422 on validate
  // and commit of a `customers` job whose CREATOR can no longer add customers.
  // It is about the job's creator, not the reader, so it reads as its own
  // sentence and never as the reader's 403.
  import_importer_not_permitted: "importer_not_permitted",
};

function problemBag(error: unknown): Record<string, unknown> | undefined {
  return error instanceof RequestError ? error.problem : undefined;
}

/**
 * Classify a thrown import failure into something a factory admin can act on.
 * Falls back to `generic` (the shared §4 error card) rather than guessing, and
 * a 404/403 without a code still classifies by status so the screen never shows
 * "something went wrong" for a plainly-explainable refusal.
 */
export function classifyImportProblem(error: unknown): ImportProblem | undefined {
  if (error === null || error === undefined) return undefined;
  const requestId = error instanceof RequestError ? error.requestId : undefined;
  const status = error instanceof RequestError ? error.httpStatus : 0;
  const code = error instanceof RequestError ? error.code : undefined;
  const byCode = code !== undefined ? PROBLEM_KINDS[code.toLowerCase()] : undefined;
  const kind: ImportProblem["kind"] =
    byCode ?? (status === 404 ? "not_found" : status === 403 ? "forbidden" : "generic");

  const bag = problemBag(error);
  const missing = bag?.["missing"];
  const owner = bag?.["owner_module"];
  return {
    kind,
    fields: Array.isArray(missing) ? missing.filter((v): v is string => typeof v === "string") : [],
    ownerModule: typeof owner === "string" ? owner : undefined,
    requestId,
  };
}

// ---------------------------------------------------------------------------
// Upload-side checks (client mirror of the D-025 caps + §9.3 formats)
// ---------------------------------------------------------------------------

export interface FileRejection {
  readonly reason: "too_large" | "unsupported";
}

/**
 * Refuse a file the parser would refuse anyway, before spending an upload on it.
 * The server re-checks the byte cap authoritatively at parse (HeadObject +
 * capped download, §9.15) — this only saves the user a 200 MB round trip.
 */
export function rejectFile(file: { name: string; size: number }): FileRejection | undefined {
  const lower = file.name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))) return { reason: "unsupported" };
  if (file.size > MAX_FILE_BYTES) return { reason: "too_large" };
  return undefined;
}

/** Human file size for the dropzone + the detail header. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Template generation
// ---------------------------------------------------------------------------

/**
 * The header-only CSV S3 offers as the import template.
 *
 * Spec §4 S3 wants the pack's `import-templates/*.xlsx` binaries; those are
 * declared in every pack manifest but the binaries (and an endpoint to serve
 * them) are still deferred (D-032), so shipping a "download" that 404s would be
 * a lie. Instead the template is generated from the SAME field catalog the
 * server maps against, plus the tenant's live pack-installed attribute columns —
 * a file that genuinely round-trips through the importer today. UTF-8 CSV is an
 * accepted upload format (§9.3), so nothing is lost but the pre-formatting.
 *
 * The pack's attribute columns belong to the lanes whose records carry an
 * attribute set (`ATTRIBUTE_TEMPLATE_KINDS`), and to no other. Before the
 * customers lane had a catalog every other lane's template was empty, so the
 * question never arose; a customers template carrying `attr.cross_section_mm2`
 * would invite a sales manager to fill a column no customer has.
 */
export function buildTemplateCsv(kind: ImportKind, attributeKeys: readonly string[] = []): string {
  const base = TARGET_FIELDS[kind] ?? [];
  const attrs = ATTRIBUTE_TEMPLATE_KINDS.includes(kind)
    ? attributeKeys.map((key) => `attr.${key}`)
    : [];
  const headers = [...base, ...attrs.filter((a) => !base.includes(a))];
  return `${headers.map(csvCell).join(",")}\n`;
}

function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function templateFileName(kind: ImportKind): string {
  return `assessly-${kind}-template.csv`;
}

// ---------------------------------------------------------------------------
// Pre-commit impact (§4 S4 "say what Commit will do")
// ---------------------------------------------------------------------------

export interface CommitImpact {
  /** Rows that will be written through the owning module. */
  readonly willLoad: number;
  /** Error rows the commit will DROP (only reachable with allow_partial). */
  readonly willSkip: number;
  /** Rows carrying a warning — they load, but somebody should look. */
  readonly withWarnings: number;
  /** What happens to a row whose natural key already exists (§2.6). */
  readonly duplicatePolicy: DuplicatePolicy;
}

/**
 * What pressing Commit will do to the business, derived from the row counts the
 * server already published — not a guess and not a promise about `created` vs
 * `updated`, which only the owning module's committer knows (its natural-key
 * lookup happens inside the commit). Saying "N rows will load under policy P,
 * M error rows will be dropped" is the honest maximum this contract supports.
 */
export function commitImpact(
  job: Pick<ImportJob, "total_rows" | "error_rows" | "warning_rows" | "duplicate_policy">,
  allowPartial: boolean,
): CommitImpact {
  const total = Math.max(0, job.total_rows);
  const errors = Math.max(0, job.error_rows);
  return {
    willLoad: Math.max(0, total - errors),
    willSkip: allowPartial ? errors : 0,
    withWarnings: Math.max(0, job.warning_rows),
    duplicatePolicy: job.duplicate_policy,
  };
}

/** Attribute keys of an attribute schema's compiled JSON schema (`properties`),
 * used to pre-add the pack's attribute columns to the products template. */
export function attributeKeysOf(jsonSchema: Record<string, unknown> | undefined): string[] {
  const properties = jsonSchema?.["properties"];
  if (properties === null || typeof properties !== "object") return [];
  return Object.keys(properties);
}
